<script setup>
  import {computed, inject, onMounted, ref, watch} from "vue";

  import ComponentIcon from "@components/Icons/Icon.vue";
  import {Deferred as InertiaDeferred, router, usePage} from "@inertiajs/vue3";
  import SymptomTableRow from "@pages/Records/Components/Sections/SymptomBar/Components/Row.vue";
  import SymptomSkeletonRow from "@pages/Records/Components/Sections/SymptomBar/Components/SkeletonRow.vue";
  import {MedButton, MedCheckbox} from "@pharma4u/medicheck-components";
  import {cn} from "@pharma4u/medicheck-components/utils";
  import {delayWhileEditing} from "@utils/Helpers/DelayWhileEditing.js";
  import {sortList} from "@utils/Helpers/ListSorter.js";
  import {cloneDeep, debounce, isEqual} from "es-toolkit";
  import Sortable from "sortablejs";

  defineOptions({name: "SymptomTable"});

  const props = defineProps({
    isEditable: {
      type: Boolean,
      required: true,
    },
    recordData: {
      type: Object,
      required: true,
    },
    mode: {
      type: String,
      default: null,
    },
  });

  const page = usePage();

  const setIsMassUpdateProcessing = inject("setIsMassUpdateProcessing");
  const deleteDialog = inject("recordEditDeleteRowDialog");

  const refDraggable = ref(null);

  const isDragging = ref(false);
  const symptoms = ref([...props.recordData.symptoms]);
  const pendingSortOrder = ref(null);
  const sortable = ref(null);
  const sortCancelToken = ref(null);
  const selectedRows = ref([]);

  const getSymptomIds = (items) => items.map((symptom) => symptom.id);

  onMounted(() => {
    initSortable();
  });

  const initSortable = () => {
    if (sortable.value !== null) {
      return; // already initialized
    }
    if (refDraggable.value === null) {
      return; // refDraggable is not available yet
    }
    sortable.value = Sortable.create(refDraggable.value, {
      handle: "[drag-handle]",
      draggable: "[drag-item]",
      direction: "vertical",
      ghostClass: "sortable-ghost",
      dataIdAttr: "drag-item", // requires `drag-item="{{item.id}}"`
      store: {
        /**
         * Save the order of elements. Called onEnd (when the item is dropped).
         * @param {Sortable} sortable
         */
        set: (sortable) => {
          // sort medications according to new sort order
          updateOrder(sortable.toArray());
        },
      },
      onStart: () => {
        isDragging.value = true;
      },
      onEnd: () => {
        isDragging.value = false;
      },
    });
  };

  const sortDirection = computed(() => {
    if (symptoms.value?.length > 0) {
      const copyOfSymptoms = cloneDeep(symptoms.value);

      const symptomIds = copyOfSymptoms.map((symptom) => symptom.id);
      const ascSortedObject = sortList(copyOfSymptoms, "symptom", "asc");

      if (isEqual(symptomIds, ascSortedObject.sortedIds)) {
        return "asc";
      }

      if (isEqual(symptomIds, ascSortedObject.sortedIds.toReversed())) {
        return "desc";
      }
    }

    return "";
  });

  const newSortDirection = computed(() => {
    if (sortDirection.value === "" || sortDirection.value === "desc") {
      return "asc";
    }

    if (sortDirection.value === "asc") {
      return "desc";
    }

    return "";
  });

  watch(
    () => props.recordData.symptoms,
    (newSymptoms) => {
      const incomingSortOrder = getSymptomIds(newSymptoms);

      if (
        pendingSortOrder.value !== null &&
        newSymptoms.length === symptoms.value.length &&
        !isEqual(incomingSortOrder, pendingSortOrder.value)
      ) {
        return;
      }

      symptoms.value = [...newSymptoms];

      if (pendingSortOrder.value !== null && isEqual(incomingSortOrder, pendingSortOrder.value)) {
        pendingSortOrder.value = null;
      }
    },
  );

  watch(
    () => symptoms.value,
    (value, oldValue) => {
      if (value.length !== oldValue?.length) {
        initSortable();
      }

      // remove deleted ids from selectedRows
      const newIds = new Set(value.map((symptom) => symptom.id));
      selectedRows.value = selectedRows.value.filter((id) => newIds.has(id));
    },
  );

  const updateSymptoms = () => {
    setIsMassUpdateProcessing("symptom", true);
    putSymptoms();
  };

  const putSymptoms = debounce(
    () => {
      delayWhileEditing(refDraggable, () => {
        router.patch(
          route("symptoms.mass-update", {
            patient: page.props.patient.id,
            record: page.props.record.id,
          }),
          {
            ...symptoms.value,
          },
          {
            preserveScroll: true,
            only: ["flash", "errors", "symptoms"],
            onFinish: () => {
              setIsMassUpdateProcessing("symptom", false);
            },
          },
        );
      });
    },
    500,
    {trailing: true},
  );

  const updateOrder = (newSortOrder) => {
    const currentSortOrder = getSymptomIds(symptoms.value);

    if (isEqual(currentSortOrder, newSortOrder)) {
      return;
    }

    pendingSortOrder.value = [...newSortOrder];

    symptoms.value = symptoms.value.toSorted((a, b) => {
      return newSortOrder.indexOf(a.id) - newSortOrder.indexOf(b.id);
    });

    if (sortCancelToken.value) {
      sortCancelToken.value.cancel();
    }

    // lock immediately (not inside the debounced request) so adding/removing a
    // symptom can't race with the pending sort request and desync the ids snapshot
    setIsMassUpdateProcessing("symptom", true);

    updateOrderRequest(newSortOrder);
  };

  const updateOrderRequest = debounce((newSortOrder) => {
    router.post(
      route("sort.store", {
        patient: page.props.patient.id,
        record: page.props.record.id,
        relation: "symptoms",
      }),
      {
        ids: newSortOrder,
      },
      {
        preserveScroll: true,
        only: ["flash", "symptoms"],
        onFinish: () => {
          setIsMassUpdateProcessing("symptom", false);
        },
        onCancelToken: (cancelToken) => (sortCancelToken.value = cancelToken),
      },
    );
  }, 2000);

  const sortByName = () => {
    if (!props.isEditable) return;

    const sortedObject = sortList(symptoms.value, "symptom", newSortDirection.value);

    updateOrder(sortedObject.sortedIds);
  };

  const showDeleteConfirmDialog = () => {
    deleteDialog({routeName: "symptoms", payload: {symptom: selectedRows.value}});
  };

  const selectAllState = computed({
    get: () => {
      if (selectedRows.value.length === symptoms.value.length) return true;
      if (selectedRows.value.length > 0) return "indeterminate";
      return false;
    },
    set: (value) => {
      selectedRows.value = value ? symptoms.value.map((s) => s.id) : [];
    },
  });

  const toggleRow = (id) => {
    const index = selectedRows.value.indexOf(id);
    if (index !== -1) {
      selectedRows.value.splice(index, 1);
    } else {
      selectedRows.value.push(id);
    }
  };
</script>

<template>
  <InertiaDeferred data="symptoms">
    <template #fallback />

    <template v-if="symptoms.length > 0">
      <!-- ☑️ multiselect actions -->
      <section v-if="mode === 'multi-select' && selectedRows?.length" class="items-top mb-2 flex space-x-1">
        <div class="my-2 rounded border-2 border-transparent bg-gray-200 px-1 py-0 text-xs font-semibold text-gray-700">
          {{ selectedRows?.length }}
        </div>

        <div class="flex flex-wrap gap-0.5">
          <MedButton
            flat
            icon="delete"
            :data-test="getTestId('default-head-bulk-delete-button')"
            @click="showDeleteConfirmDialog()"
          >
            Löschen
          </MedButton>
        </div>
      </section>

      <div class="grid grid-cols-12 items-end gap-x-6 pb-2 text-sm font-normal text-gray-700">
        <div class="col-span-4 flex items-center">
          <template v-if="isEditable">
            <div class="flex size-6 shrink-0 items-center justify-center stroke-0">
              <MedCheckbox
                v-if="mode === 'multi-select'"
                v-model="selectAllState"
                aria-label="Alle Zeilen auswählen"
                variant="primary"
                :data-test="getTestId('table-select-all-checkbox')"
              />
            </div>

            <button :class="cn('ml-2 flex items-center text-gray-700 hover:no-underline')" @click.prevent="sortByName">
              <template v-if="sortDirection === 'asc'">
                <component-icon>expand_more</component-icon>
              </template>

              <template v-else-if="sortDirection === 'desc'">
                <component-icon>expand_less</component-icon>
              </template>

              <template v-else>
                <component-icon>unfold_more</component-icon>
              </template>

              <span>Problem / Symptom</span>
            </button>
          </template>
          <template v-else>
            <span class="ml-2">Problem / Symptom</span>
          </template>
        </div>

        <div class="col-span-2">Seit (Datum; opt.)</div>
        <div class="col-span-2">Vermutung</div>
        <div class="col-span-3">Warum? / Was?</div>
        <div class="col-span-1" />
      </div>
    </template>
  </InertiaDeferred>

  <ul ref="refDraggable">
    <InertiaDeferred :data="['symptoms', 'medications', 'diseases', 'laborvalues']">
      <template #fallback>
        <template v-for="i in 5" :key="i">
          <SymptomSkeletonRow />
        </template>
      </template>

      <template v-for="(symptom, index) in symptoms" :key="symptom.id">
        <SymptomTableRow
          :drag-item="symptom.id"
          :is-editable="isEditable"
          :symptom="symptom"
          :is-dragging="isDragging"
          :test-id="getTestId(`table-${index}`)"
          @update-symptoms="updateSymptoms"
        >
          <template v-if="mode === 'multi-select'" #prefix>
            <div class="flex size-6 shrink-0 items-center justify-center stroke-0">
              <MedCheckbox
                :model-value="selectedRows.includes(symptom.id)"
                :aria-label="`Zeile ${symptom.symptom} auswählen`"
                variant="primary"
                :data-test="getTestId('table-row-select-checkbox')"
                @update:model-value="toggleRow(symptom.id)"
              />
            </div>
          </template>
        </SymptomTableRow>
      </template>
    </InertiaDeferred>
  </ul>
</template>
